import {
  MarketRateScope,
  MarketShippingConfiguration,
  ShippingRateGroupFe,
  ShippingTestMethodDefinitionDto,
  VariationFe,
} from "@intelligems/ig-types";
import {
  isSharedRateScope,
  marketRateScopeKey,
  marketRateScopesForSelections,
  serviceNameCollisions,
} from "@intelligems/ig-utils";
import {
  marketTargets,
  marketUnavailableReason,
  rateGroupOriginsLabel,
  rateGroupProductsLabel,
} from "./selection";

/** One coverage a merchant can author replacement rates for. */
export interface RateScopeChoice {
  key: string;
  scope: MarketRateScope;
  /** Market and products, e.g. "United States · Apparel". */
  label: string;
  /** Origin locations, e.g. "Ships from: Warehouse A". */
  sublabel: string;
  marketName: string;
  /** "Apparel — Ships from: Warehouse A": the coverage without its market. */
  coverageLabel: string;
}

/** Every rate group of an active, supported option in an available market. */
const addOnlyRateGroups = (markets: MarketShippingConfiguration[]) =>
  markets.flatMap((market) =>
    marketUnavailableReason(market)
      ? []
      : (market.shipping?.options ?? []).flatMap((option) => {
          const optionType = option.optionType;
          return option.isActive && optionType !== "unsupported"
            ? option.rateGroups.map((group) => ({
                market,
                option,
                optionType,
                group,
              }))
            : [];
        })
  );

/** A coverage shown in "Applies to" that a rate cannot be placed at. */
export interface UnavailableRateScope {
  key: string;
  marketName: string;
  coverageLabel: string;
  reason: string;
}

/**
 * Add-only coverages in the chosen markets that Intelligems rates cannot
 * serve, so "Applies to" can list them disabled with why. A Shopify rate
 * group with no origin locations matches no shipment (unlike collections,
 * there is no "all locations"), so a carrier placed there never shows.
 * Replacement choices come from the selections, so there are none then.
 */
export const unavailableRateScopes = (
  selections: ShippingTestMethodDefinitionDto[],
  markets: MarketShippingConfiguration[]
): UnavailableRateScope[] => {
  if (marketTargets(selections).length) {
    return [];
  }
  const unavailable = new Map<string, UnavailableRateScope>();
  for (const { market, group } of addOnlyRateGroups(markets)) {
    if (group.conditions.originLocations.length) {
      continue;
    }
    const key = marketRateScopeKey({
      marketId: market.id,
      collectionIds:
        group.conditions.collections?.map((item) => item.id) ?? null,
      originLocationIds: [],
    });
    unavailable.set(key, {
      key,
      marketName: market.name || market.id,
      coverageLabel: `${rateGroupProductsLabel(group)} — Ships from: ${rateGroupOriginsLabel(group)}`,
      reason:
        "No ships-from locations in Shopify, so rates here never show at checkout",
    });
  }
  return [...unavailable.values()];
};

/**
 * The coverages the selected Shopify rates remove, one per distinct
 * (market, collections, origins) tuple. This mirrors how the backend places
 * the Intelligems carrier. Without selections, every active coverage in the
 * chosen markets is available for an add-only carrier placement.
 */
export const rateScopeChoices = (
  selections: ShippingTestMethodDefinitionDto[],
  markets: MarketShippingConfiguration[]
): RateScopeChoice[] => {
  const options = markets.flatMap((market) =>
    (market.shipping?.options ?? []).map((option) => ({
      marketId: market.id,
      optionId: option.id,
      rateGroups: option.rateGroups.map((group) => ({
        id: group.id,
        collectionIds:
          group.conditions.collections === null
            ? null
            : group.conditions.collections.map((item) => item.id),
        originLocationIds: group.conditions.originLocations.map(
          (item) => item.id
        ),
      })),
    }))
  );
  const targets = marketTargets(selections);
  const coverages = targets.length
    ? targets
    : addOnlyRateGroups(markets)
        .filter(({ group }) => group.conditions.originLocations.length)
        .map(({ market, option, optionType, group }) => ({
          marketId: market.id,
          optionId: option.id,
          optionType,
          rateGroupId: group.id,
          kind: "rate_group" as const,
        }));
  return marketRateScopesForSelections(coverages, options).map((derived) => {
    const market = markets.find((item) => item.id === derived.scope.marketId);
    const source = derived.sources[0];
    const group = market?.shipping?.options
      .find((option) => option.id === source?.optionId)
      ?.rateGroups.find((item) => item.id === source?.rateGroupId);
    const marketName = market?.name || derived.scope.marketId;
    const products = group
      ? rateGroupProductsLabel(group)
      : "Selected products";
    const sublabel = group
      ? `Ships from: ${rateGroupOriginsLabel(group)}`
      : "Selected locations";
    return {
      key: derived.key,
      scope: derived.scope,
      label: `${marketName} · ${products}`,
      sublabel,
      marketName,
      coverageLabel: `${products} — ${sublabel}`,
    };
  });
};

/** Rate groups that serve at a placement in `scopeKeys`: shared or listing one. */
export const rateGroupsServing = (
  groups: ShippingRateGroupFe[] | null | undefined,
  scopeKeys: Set<string>
): ShippingRateGroupFe[] =>
  (groups ?? []).filter(
    (group) =>
      isSharedRateScope(group.scopes) ||
      group.scopes!.some((scope) => scopeKeys.has(marketRateScopeKey(scope)))
  );

/**
 * Why the scoped rates cannot start as authored, or null. Mirrors the two
 * refusals in the backend's placement planning so the merchant hears about
 * them before saving rather than on start:
 * - a scoped rate group listing a coverage the selections no longer remove;
 * - a selected coverage that no rate (listing it, or shared) serves for some
 *   test group, once any group in that test group is scoped.
 */
export const orphanedScopeReason = (
  variations: VariationFe[],
  choices: RateScopeChoice[],
  addOnly = false
): string | null => {
  const served = new Set(choices.map((choice) => choice.key));
  const required = addOnly
    ? new Set(
        variations.flatMap((variation) =>
          (variation.shippingRateGroups ?? []).flatMap((group) =>
            (group.scopes ?? []).map(marketRateScopeKey)
          )
        )
      )
    : served;
  for (const variation of variations) {
    const groups = variation.shippingRateGroups ?? [];
    if (addOnly && groups.some((group) => isSharedRateScope(group.scopes))) {
      return `${variation.name || "A test group"} needs an Applies to coverage for every rate.`;
    }
    const scoped = groups.filter((group) => !isSharedRateScope(group.scopes));
    if (
      scoped.some((group) =>
        group.scopes!.some((scope) => !served.has(marketRateScopeKey(scope)))
      )
    ) {
      return "A test rate replaces Shopify coverage that is no longer selected. Re-select that coverage or edit the test rates.";
    }
    if (!scoped.length) {
      continue;
    }
    const covered = new Set(
      scoped.flatMap((group) => group.scopes!.map(marketRateScopeKey))
    );
    const hasShared = groups.length > scoped.length;
    const unserved = choices.find(
      (choice) =>
        required.has(choice.key) && !hasShared && !covered.has(choice.key)
    );
    if (unserved) {
      return `${variation.name || "A test group"} has no rate for ${unserved.label}. Add one there or a shared rate.`;
    }
  }
  return null;
};

/**
 * Why the rates cannot start because two of them with different "Applies to"
 * share a checkout name, or null. Shopify admits Intelligems rates at a
 * coverage by name, so a shared name would show one coverage's rate at the
 * other's. Mirrors the backend's placement planning refusal; it spans every
 * test group because a placement admits every group's names.
 */
export const serviceNameCollisionReason = (
  variations: VariationFe[]
): string | null => {
  const collision = serviceNameCollisions(
    variations.flatMap((variation) =>
      (variation.shippingRateGroups ?? []).map((group) => ({
        ...group,
        variationId: variation.id,
        rates: group.rates.filter((rate) => rate.name?.trim()),
      }))
    )
  )[0];
  return collision
    ? "Rates with different Applies to settings need unique names."
    : null;
};

/** The first reason scoped Markets rates cannot start as authored, or null. */
export const scopedRatesReason = (
  variations: VariationFe[],
  choices: RateScopeChoice[],
  addOnly = false
): string | null =>
  orphanedScopeReason(variations, choices, addOnly) ??
  serviceNameCollisionReason(variations);
